import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { getAuthRedirectUrl } from "@/lib/edge-functions";
import { useAuth } from "@/hooks/useAuth";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useOrgSettings } from "@/hooks/useOrgSettings";

const HOUR_IN_MS = 60 * 60 * 1000;

const clampTimeoutHours = (hours?: number) => {
  if (!hours) return 0;
  if (hours < 0) return 0;
  return hours;
};

export function useSessionTimeout() {
  const { session, signOut } = useAuth();
  const { isStaff, isLoading: rolesLoading } = useUserRoles();
  const { settings } = useOrgSettings();

  // Tracks the timestamp from which the timeout window should be measured
  // when a user is promoted to a staff role mid-session.  Without this,
  // a promotion would immediately trigger a timeout because last_sign_in_at
  // (set at the original login) may already be older than the timeout window.
  const staffPromotedAtRef = useRef<number | null>(null);

  // Previous stable value of isStaff (undefined = roles haven't loaded yet).
  const prevIsStaffRef = useRef<boolean | undefined>(undefined);
  const prevSessionUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Don't track transitions until roles have fully loaded to avoid
    // treating the initial loading→loaded transition as a promotion.
    if (rolesLoading) {
      return;
    }

    const currentUserId = session?.user?.id;

    // Session bootstrap begins with no user; don't persist role state yet
    // or we'll misclassify initial load as an in-session promotion.
    if (!currentUserId) {
      prevIsStaffRef.current = undefined;
      prevSessionUserIdRef.current = undefined;
      staffPromotedAtRef.current = null;
      return;
    }

    // Reset transition tracking when the authenticated user changes.
    if (prevSessionUserIdRef.current !== currentUserId) {
      prevSessionUserIdRef.current = currentUserId;
      prevIsStaffRef.current = undefined;
      staffPromotedAtRef.current = null;
    }

    const wasStaff = prevIsStaffRef.current;
    prevIsStaffRef.current = isStaff;

    if (!isStaff) {
      staffPromotedAtRef.current = null;
      return;
    }

    // Detect mid-session promotion: isStaff went from false → true after
    // the initial load (wasStaff was explicitly false, not undefined).
    // Give the newly-promoted user a fresh timeout window starting now.
    if (wasStaff === false) {
      staffPromotedAtRef.current = Date.now();
    }

    const timeoutHours = clampTimeoutHours(settings.staffSessionTimeoutHours ?? 12);
    if (timeoutHours === 0) {
      return;
    }

    const lastSignInAt = session.user?.last_sign_in_at;
    if (!lastSignInAt) {
      return;
    }

    const lastSignInTime = new Date(lastSignInAt).getTime();
    if (Number.isNaN(lastSignInTime)) {
      return;
    }

    // Use the later of last_sign_in_at and the promotion timestamp so that
    // mid-session promotions don't trigger an immediate timeout.
    const baseTime = staffPromotedAtRef.current
      ? Math.max(lastSignInTime, staffPromotedAtRef.current)
      : lastSignInTime;
    const expiresAt = baseTime + timeoutHours * HOUR_IN_MS;
    const remainingMs = expiresAt - Date.now();

    if (remainingMs <= 0) {
      // The sign-out loads the sign-in page afresh (#7026), which says the session expired and
      // returns here after sign-in. The toast is for when it can't: a failed sign-out emits no
      // SIGNED_OUT, so nothing reloads and the page would otherwise say nothing (Codex on #7027).
      void signOut({ redirectTo: getAuthRedirectUrl(undefined, "session-expired") });
      toast.info("Your session timed out. Please sign in again.");
      return;
    }

    const timeoutId = window.setTimeout(() => {
      // The sign-out loads the sign-in page afresh (#7026), which says the session expired and
      // returns here after sign-in. The toast is for when it can't: a failed sign-out emits no
      // SIGNED_OUT, so nothing reloads and the page would otherwise say nothing (Codex on #7027).
      void signOut({ redirectTo: getAuthRedirectUrl(undefined, "session-expired") });
      toast.info("Your session timed out. Please sign in again.");
    }, remainingMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    session?.user?.id,
    session?.user?.last_sign_in_at,
    isStaff,
    rolesLoading,
    settings.staffSessionTimeoutHours,
    signOut,
  ]);
}
